// =============================================================================
// BP-07-03: ResourceCard Component
// =============================================================================

import { Link } from 'react-router-dom';
import {
  FileText,
  BookOpen,
  PlayCircle,
  CheckSquare,
  FileCode,
  ExternalLink,
  Clock,
  Eye,
  Star,
} from 'lucide-react';
import { SaveButton } from './SaveButton';
import type { Resource, ResourceType } from '@/features/resources/types/resourcesTypes';
import { RESOURCE_TYPE_LABELS } from '@/features/resources/types/resourcesTypes';
import { cn } from '@/core/lib/utils';

// -----------------------------------------------------------------------------
// Types
// -----------------------------------------------------------------------------

interface ResourceCardProps {
  resource: Resource;
  variant?: 'default' | 'compact' | 'horizontal';
  showCategory?: boolean;
  showDiagnosis?: boolean;
  className?: string;
  onSaveToggle?: (resourceId: string, isSaved: boolean) => void;
}

// -----------------------------------------------------------------------------
// Icon Mapping
// -----------------------------------------------------------------------------

const typeIcons: Record<ResourceType, typeof FileText> = {
  article: FileText,
  guide: BookOpen,
  video: PlayCircle,
  checklist: CheckSquare,
  template: FileCode,
  external: ExternalLink,
};

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

export function ResourceCard({
  resource,
  variant = 'default',
  showCategory = true,
  showDiagnosis = false,
  className,
  onSaveToggle,
}: ResourceCardProps) {
  const TypeIcon = typeIcons[resource.resourceType];
  const isExternal = resource.resourceType === 'external';
  
  const CardWrapper = isExternal ? 'a' : Link;
  const cardProps = isExternal
    ? { href: resource.videoUrl || '#', target: '_blank', rel: 'noopener noreferrer' }
    : { to: `/resources/${resource.slug}` };

  if (variant === 'compact') {
    return (
      <CardWrapper
        {...cardProps}
        className={cn(
          'group flex items-center gap-3 p-3 rounded-lg',
          'bg-white border border-warm-gray/10',
          'hover:shadow-soft hover:border-sage-light/30 transition-all duration-200',
          className
        )}
      >
        <div className="flex-shrink-0 w-10 h-10 rounded-md bg-sage/10 flex items-center justify-center">
          <TypeIcon className="w-5 h-5 text-sage" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-navy truncate group-hover:text-sage-dark">
            {resource.title}
          </h4>
          <p className="text-xs text-warm-gray">
            {RESOURCE_TYPE_LABELS[resource.resourceType]}
            {resource.readingTimeMinutes && ` · ${resource.readingTimeMinutes} min read`}
          </p>
        </div>
        {isExternal && <ExternalLink className="w-4 h-4 text-warm-gray flex-shrink-0" />}
      </CardWrapper>
    );
  }

  if (variant === 'horizontal') {
    return (
      <CardWrapper
        {...cardProps}
        className={cn(
          'group flex gap-4 p-4 rounded-xl',
          'bg-white border border-warm-gray/10',
          'hover:shadow-medium hover:border-sage-light/30 transition-all duration-200',
          className
        )}
      >
        {/* Image */}
        {resource.featuredImageUrl ? (
          <div className="flex-shrink-0 w-32 h-24 rounded-lg overflow-hidden bg-cream-dark">
            <img
              src={resource.featuredImageUrl}
              alt=""
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          </div>
        ) : (
          <div className="flex-shrink-0 w-32 h-24 rounded-lg bg-cream-dark flex items-center justify-center">
            <TypeIcon className="w-8 h-8 text-warm-gray/50" />
          </div>
        )}

        {/* Content */}
        <div className="flex-1 min-w-0 flex flex-col">
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-center gap-2 text-xs text-terracotta font-medium">
              <TypeIcon className="w-3.5 h-3.5" />
              <span>{RESOURCE_TYPE_LABELS[resource.resourceType]}</span>
            </div>
            <SaveButton
              resourceId={resource.id}
              isSaved={resource.isSaved}
              size="sm"
              onToggle={onSaveToggle}
            />
          </div>
          
          <h3 className="mt-1 font-display font-medium text-navy group-hover:text-sage-dark line-clamp-1">
            {resource.title}
          </h3>
          
          {resource.excerpt && (
            <p className="mt-1 text-sm text-warm-gray line-clamp-2">
              {resource.excerpt}
            </p>
          )}

          <div className="mt-auto pt-2 flex items-center gap-3 text-xs text-warm-gray">
            {resource.readingTimeMinutes && (
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {resource.readingTimeMinutes} min
              </span>
            )}
            {resource.viewCount > 0 && (
              <span className="flex items-center gap-1">
                <Eye className="w-3.5 h-3.5" />
                {resource.viewCount.toLocaleString()}
              </span>
            )}
            {resource.avgRating && (
              <span className="flex items-center gap-1">
                <Star className="w-3.5 h-3.5 fill-terracotta text-terracotta" />
                {resource.avgRating.toFixed(1)}
              </span>
            )}
          </div>
        </div>
      </CardWrapper>
    );
  }

  // Default card variant
  return (
    <CardWrapper
      {...cardProps}
      className={cn(
        'group flex flex-col rounded-xl overflow-hidden',
        'bg-white border border-warm-gray/10',
        'hover:shadow-lifted hover:-translate-y-1 transition-all duration-300',
        className
      )}
    >
      {/* Image */}
      <div className="relative aspect-[16/9] bg-cream-dark overflow-hidden">
        {resource.featuredImageUrl ? (
          <img
            src={resource.featuredImageUrl}
            alt=""
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <TypeIcon className="w-12 h-12 text-warm-gray/30" />
          </div>
        )}
        
        {/* Type Badge */}
        <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-white/90 backdrop-blur-sm">
          <span className="flex items-center gap-1.5 text-xs font-medium text-terracotta">
            <TypeIcon className="w-3.5 h-3.5" />
            {RESOURCE_TYPE_LABELS[resource.resourceType]}
          </span>
        </div>

        {/* Save Button */}
        <div className="absolute top-3 right-3">
          <SaveButton
            resourceId={resource.id}
            isSaved={resource.isSaved}
            variant="ghost"
            onToggle={onSaveToggle}
          />
        </div>

        {/* Video Indicator */}
        {resource.resourceType === 'video' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-14 h-14 rounded-full bg-white/90 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform">
              <PlayCircle className="w-8 h-8 text-terracotta" />
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 p-4 flex flex-col">
        {/* Category / Diagnosis */}
        {(showCategory || showDiagnosis) && (
          <div className="flex flex-wrap gap-1.5 mb-2">
            {showCategory && resource.category && (
              <span className="px-2 py-0.5 text-xs font-medium text-sage-dark bg-sage/10 rounded-full">
                {resource.category.name}
              </span>
            )}
            {showDiagnosis && resource.diagnoses?.slice(0, 2).map((diagnosis) => (
              <span
                key={diagnosis.id}
                className="px-2 py-0.5 text-xs font-medium text-navy-light bg-navy/5 rounded-full"
              >
                {diagnosis.shortName || diagnosis.name}
              </span>
            ))}
          </div>
        )}

        {/* Title */}
        <h3 className="font-display font-medium text-lg text-navy group-hover:text-sage-dark line-clamp-2">
          {resource.title}
        </h3>

        {/* Excerpt */}
        {resource.excerpt && (
          <p className="mt-2 text-sm text-warm-gray line-clamp-2">
            {resource.excerpt}
          </p>
        )}

        {/* Meta */}
        <div className="mt-auto pt-3 flex items-center justify-between text-xs text-warm-gray">
          <div className="flex items-center gap-3">
            {resource.readingTimeMinutes && (
              <span className="flex items-center gap-1">
                <Clock className="w-3.5 h-3.5" />
                {resource.readingTimeMinutes} min
              </span>
            )}
            {resource.viewCount > 0 && (
              <span className="flex items-center gap-1">
                <Eye className="w-3.5 h-3.5" />
                {resource.viewCount.toLocaleString()}
              </span>
            )}
          </div>
          
          {resource.avgRating && resource.ratingCount > 0 && (
            <span className="flex items-center gap-1">
              <Star className="w-3.5 h-3.5 fill-terracotta text-terracotta" />
              <span className="font-medium text-navy">{resource.avgRating.toFixed(1)}</span>
              <span>({resource.ratingCount})</span>
            </span>
          )}
        </div>
      </div>

      {/* External Link Indicator */}
      {isExternal && (
        <div className="px-4 pb-3">
          <div className="flex items-center gap-1 text-xs text-warm-gray">
            <ExternalLink className="w-3.5 h-3.5" />
            <span>Opens in new tab</span>
          </div>
        </div>
      )}
    </CardWrapper>
  );
}


export function ResourceCardCompact(props: any) {
  return <ResourceCard {...props} variant="compact" />;
}

export function ResourceCardHorizontal(props: any) {
  return <ResourceCard {...props} variant="horizontal" />;
}

export default ResourceCard;


